/**
 * Controles físicos del monitor: brillo / contraste / reset, mute y
 * perfil de pantalla.
 *
 * Persistencia en localStorage con dos claves:
 *  - `51esc.crt`   → { brt: number, cnt: number, profile: MonitorProfileId }
 *  - `51esc.audio` → 'muted' | 'on'
 *
 * Los rangos de brt/cnt están limitados (brt 0.4-1.6, cnt 0.5-1.8) para
 * no llegar a "pantalla blanca cegadora" o "pantalla apagada total" —
 * sería realista pero molesto en una web.
 *
 * Migración soft: si el JSON guardado no incluye `profile` (cuentas
 * anteriores a 12a-2), se asume DEFAULT_PROFILE_ID. Sin necesidad de
 * migración hard porque la lectura es best-effort.
 */

import * as audio from './audio.js';
import {
  DEFAULT_PROFILE_ID,
  isMonitorProfileId,
  type MonitorProfileId,
} from './monitor-profiles.js';

const STORAGE_CRT = '51esc.crt';
const STORAGE_AUDIO = '51esc.audio';
const STEP = 0.1;

/**
 * Modo de día/noche para la sala de operaciones:
 *  - 'auto': detección automática por hora del sistema (default).
 *  - 'day' / 'dusk' / 'night': forzar manualmente. URL `?time=` lo
 *    sigue sobrescribiendo (útil para testing y screenshots).
 *  - 'cycle-10' / 'cycle-30' / 'cycle-60': rota día → tarde → noche
 *    cada N segundos. Útil para presumir del crossfade. El ciclador
 *    lo gestiona `scene/time-of-day-cycle.ts`.
 */
export type TimeOfDayPreference =
  | 'auto'
  | 'day'
  | 'dusk'
  | 'night'
  | 'cycle-10'
  | 'cycle-30'
  | 'cycle-60';

const DEFAULTS = {
  brt: 1.0,
  cnt: 1.0,
  profile: DEFAULT_PROFILE_ID,
  timeOfDay: 'auto' as TimeOfDayPreference,
} as const;

interface CrtState {
  brt: number;
  cnt: number;
  profile: MonitorProfileId;
  timeOfDay: TimeOfDayPreference;
}

function isTimeOfDayPreference(v: unknown): v is TimeOfDayPreference {
  return (
    v === 'auto' ||
    v === 'day' ||
    v === 'dusk' ||
    v === 'night' ||
    v === 'cycle-10' ||
    v === 'cycle-30' ||
    v === 'cycle-60'
  );
}

function loadCrtState(): CrtState {
  try {
    const raw = localStorage.getItem(STORAGE_CRT);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<CrtState>;
    const brt = typeof parsed.brt === 'number' ? parsed.brt : DEFAULTS.brt;
    const cnt = typeof parsed.cnt === 'number' ? parsed.cnt : DEFAULTS.cnt;
    const profile = isMonitorProfileId(parsed.profile) ? parsed.profile : DEFAULTS.profile;
    const timeOfDay = isTimeOfDayPreference(parsed.timeOfDay)
      ? parsed.timeOfDay
      : DEFAULTS.timeOfDay;
    return { brt, cnt, profile, timeOfDay };
  } catch {
    /* ignore — vuelve a defaults */
  }
  return { ...DEFAULTS };
}

function saveCrtState(state: CrtState): void {
  try {
    localStorage.setItem(STORAGE_CRT, JSON.stringify(state));
  } catch {
    /* localStorage no disponible (modo incógnito strict, etc.) — sigue */
  }
}

/**
 * Lee solo el perfil guardado (o default si no hay nada). Lo usa
 * `index.ts` en boot para aplicar el perfil antes del primer paint
 * sin instanciar la lógica completa de brt/cnt.
 */
export function loadStoredProfile(): MonitorProfileId {
  return loadCrtState().profile;
}

/**
 * Persiste el perfil elegido por el usuario manteniendo brt/cnt. Lo usa
 * el selector de CONFIG → PERFIL DE PANTALLA al confirmar una opción.
 */
export function saveStoredProfile(profile: MonitorProfileId): void {
  const current = loadCrtState();
  saveCrtState({ ...current, profile });
}

/**
 * Lee la preferencia de día/noche persistida. La usa la sala
 * (`/`) en runtime + el inline script en `<head>` (que parsea el
 * JSON crudo de localStorage en lugar de importar este módulo).
 */
export function loadStoredTimeOfDay(): TimeOfDayPreference {
  return loadCrtState().timeOfDay;
}

/**
 * Persiste la preferencia de día/noche desde CONFIG → MODO DÍA/TARDE/NOCHE.
 */
export function saveStoredTimeOfDay(pref: TimeOfDayPreference): void {
  const current = loadCrtState();
  saveCrtState({ ...current, timeOfDay: pref });
}

function loadMuted(): boolean {
  try {
    return localStorage.getItem(STORAGE_AUDIO) === 'muted';
  } catch {
    return false;
  }
}

function saveMuted(m: boolean): void {
  try {
    localStorage.setItem(STORAGE_AUDIO, m ? 'muted' : 'on');
  } catch {
    /* ignore */
  }
}

/**
 * Conecta los botones físicos del case CRT con la lógica de
 * brillo/contraste y aplica el filtro CSS al `<div class="screen">`.
 * Idempotente — engancha listeners una sola vez.
 */
export function wireCrtControls(screen: HTMLElement, root: ParentNode): void {
  let state = loadCrtState();

  const apply = (): void => {
    screen.style.filter = `brightness(${state.brt}) contrast(${state.cnt})`;
    saveCrtState(state);
  };
  apply();

  for (const btn of root.querySelectorAll<HTMLButtonElement>('.ctrl-btn')) {
    btn.addEventListener('click', () => {
      const ctrl = btn.dataset.ctrl;
      const dir = Number(btn.dataset.dir ?? '0');
      if (ctrl === 'brt') {
        state = { ...state, brt: clamp(state.brt + dir * STEP, 0.4, 1.6) };
      } else if (ctrl === 'cnt') {
        state = { ...state, cnt: clamp(state.cnt + dir * STEP, 0.5, 1.8) };
      }
      apply();
      audio.playButtonClick();
    });
  }

  const resetBtn = root.querySelector<HTMLButtonElement>('.ctrl-reset');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      // Reset solo afecta a brt/cnt — el perfil de pantalla es
      // preferencia visual aparte y se mantiene.
      state = { ...state, brt: DEFAULTS.brt, cnt: DEFAULTS.cnt };
      apply();
      audio.playButtonClick();
    });
  }
}

/**
 * Conecta el botón ♪ (audio toggle). Persiste el estado mute/unmute,
 * actualiza la apariencia del botón y arranca el ambiente al
 * desmutar.
 */
export function wireAudioToggle(toggle: HTMLButtonElement): void {
  let mutedUI = loadMuted();
  const refresh = (): void => {
    toggle.textContent = '♪';
    toggle.classList.toggle('muted', mutedUI);
    toggle.title = mutedUI ? 'Activar sonido' : 'Silenciar sonido';
    audio.setMuted(mutedUI);
  };
  refresh();

  toggle.addEventListener('click', () => {
    mutedUI = !mutedUI;
    saveMuted(mutedUI);
    refresh();
    if (!mutedUI) {
      // Re-arranca el ambiente del perfil ACTIVO, no el sintético por
      // defecto: si estamos en Amstrad queremos su sample, no el coil
      // whine + hum + fan de IBM.
      audio.restartAmbient();
    }
    audio.playButtonClick();
  });
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
